import { useId, useState, type FormEvent } from 'react';
import { Dialog, DialogActions } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { CoverPicker } from '../ui/CoverPicker';
import { createRoom, updateRoom } from '../db/rooms';
import { ImageDecodeError } from '../db/images';
import type { Room } from '../db/schema';
import { IconPicker } from './IconPicker';
import { DEFAULT_ICON, iconByName } from './roomIcons';
import '../ui/form.css';

/** Create when `room` is absent, edit when it isn't. The form is the same. */
export function RoomDialog({ room, onClose }: { room?: Room; onClose: () => void }) {
  const nameId = useId();

  const [name, setName] = useState(room?.name ?? '');
  const [icon, setIcon] = useState(room?.icon ?? DEFAULT_ICON);
  const [cover, setCover] = useState<Blob | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed === '') {
      setError('Give the room a name.');
      return;
    }

    setBusy(true);
    setError(undefined);
    try {
      if (room === undefined) {
        await createRoom({ name: trimmed, icon, cover: cover ?? undefined });
      } else {
        await updateRoom(room.id, { name: trimmed, icon, cover });
      }
      onClose();
    } catch (thrown) {
      setError(
        thrown instanceof ImageDecodeError
          ? thrown.message
          : "That room couldn't be saved. Nothing was changed.",
      );
      setBusy(false);
    }
  }

  const Icon = iconByName(icon);

  return (
    <Dialog title={room === undefined ? 'New Room' : 'Edit Room'} onClose={onClose}>
      <form className="dialog__form" onSubmit={(event) => void submit(event)}>
        <CoverPicker
          label="Cover"
          existingImageId={room?.coverImageId}
          cover={cover}
          placeholder={<Icon size={40} aria-hidden />}
          onPick={setCover}
          onClear={() => setCover(null)}
        />

        <div className="field">
          <label className="section-label" htmlFor={nameId}>
            Name
          </label>
          <input
            id={nameId}
            className="input"
            value={name}
            placeholder="Living Room"
            autoFocus
            onChange={(event) => setName(event.target.value)}
          />
        </div>

        <div className="field">
          <span className="section-label">Icon</span>
          <IconPicker value={icon} onChange={setIcon} />
        </div>

        {error !== undefined && (
          <p className="dialog__error" role="alert">
            {error}
          </p>
        )}

        <DialogActions>
          <Button variant="quiet" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy}>
            {room === undefined ? 'Create Room' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
